import { Capacitor, registerPlugin } from '@capacitor/core';
import { apiRequest, getDeviceId } from './appApi';
import type { VerifyOtpResult } from '@/contexts/AuthContext';

export interface GoogleBinding { bound: boolean; email_masked: string | null; bound_at: number | null }
export type GoogleStep =
  | { status: 'needs_terms'; terms_version: string }
  | { status: 'needs_email'; email_masked: string; rate_seconds: number }
  | { status: 'needs_totp'; remaining_attempts: number }
  | { status: 'authenticated'; auth: VerifyOtpResult }
  | { status: 'bound' | 'unbound'; binding: GoogleBinding };
export type GoogleIntent = 'login' | 'bind' | 'unlink';
interface GoogleNative {
  signIn(options: { nonce: string; serverClientId?: string }): Promise<{ idToken: string }>;
  cancelSignIn(): Promise<void>;
  signOut(): Promise<void>;
  disconnect(): Promise<void>;
}
export const googleNative = registerPlugin<GoogleNative>('GoogleAuth');
export const isGoogleNativePlatform = () => Capacitor.isNativePlatform() && ['ios', 'android'].includes(Capacitor.getPlatform());
export const isGoogleAndroidPlatform = () => Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android';
export const isGoogleSupportedPlatform = () => !Capacitor.isNativePlatform() || isGoogleNativePlatform();
const base = '/app/auth/google';
async function request<T>(path: string, options: RequestInit, accessToken?: string | null): Promise<T> {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), 15000);
  try { return await apiRequest<T>(base + path, { ...options, signal: controller.signal }, accessToken); }
  finally { window.clearTimeout(timer); }
}
function post<T>(path: string, body: object, accessToken?: string | null) {
  return request<T>(path, { method: 'POST', body: JSON.stringify({ ...body, device_id: getDeviceId() }) }, accessToken);
}
export const googleAuth = {
  start: (intent: GoogleIntent, accessToken?: string | null, email?: string) =>
    post<{ client_id: string | null; attempt_token: string; nonce: string | null; expires_in: number; step: GoogleStep | null }>('/start', { intent, email }, accessToken),
  exchange: (attempt_token: string, id_token: string) => post<GoogleStep>('/exchange', { attempt_token, id_token }),
  sendEmail: (attempt_token: string) => post<{ rate_seconds: number }>('/email-code', { attempt_token }),
  complete: (attempt_token: string, fields: { agreed_terms?: boolean; terms_version?: string; otp?: string; totp_code?: string }) =>
    post<GoogleStep>('/complete', { attempt_token, ...fields }),
  binding: (accessToken: string) => request<GoogleBinding>('/binding', {}, accessToken),
};
